import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, Wrench, Camera } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import RepairPhotos from "@/components/RepairPhotos";
import { MechanicHeader, workState } from "@/components/mechanic/MechanicHeader";
import { MechanicTasks } from "@/components/mechanic/MechanicTasks";
import { MechanicWorklist } from "@/components/mechanic/MechanicWorklist";
import { MechanicInspection } from "@/components/mechanic/MechanicInspection";
import { MechanicParts } from "@/components/mechanic/MechanicParts";
import { MechanicNotes } from "@/components/mechanic/MechanicNotes";
import { PartsRequestPanel } from "@/components/mechanic/PartsRequestPanel";

export default function MechanicCard() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t, lang } = useLang();
  const qc = useQueryClient();
  const { data: card, refetch } = useQuery({ queryKey: ["repair", id], queryFn: () => api.get(`/repairs/${id}`).then((r) => r.data) });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: () => api.get("/settings").then((r) => r.data) });
  const { data: inventory = [] } = useQuery({ queryKey: ["inv"], queryFn: () => api.get("/inventory").then((r) => r.data) });
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { const i = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(i); }, []);

  const running = (card?.time_logs || []).find((l) => !l.stopped_at);
  const liveSec = running ? Math.max(0, Math.floor((now - new Date(running.started_at).getTime()) / 1000)) : 0;

  const done = async () => { await refetch(); qc.invalidateQueries({ queryKey: ["repairs"] }); };
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); await done(); if (msg) toast.success(msg); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const autosave = async (fn) => {
    setSaving(true);
    try { await fn(); await done(); } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  // One mechanic, one clock: stop any timer on another card before starting here.
  const start = (title) => run(async () => {
    const { data: others } = await api.get("/repairs/running/mine");
    for (const o of others.filter((x) => x.id !== id)) {
      await api.post(`/repairs/${o.id}/clock-out`, { log_id: o.log_id });
      toast.info(t("mech_stopped_other", { card: o.car_plate || o.card_number }));
    }
    if (running) await api.post(`/repairs/${id}/clock-out`, {});
    await api.post(`/repairs/${id}/clock-in`, { note: title, kind: "shop" });
  });
  const stop = (hold) => run(async () => {
    if (running) await api.post(`/repairs/${id}/clock-out`, { hold });
    else await api.put(`/repairs/${id}`, { hold: hold || "none", status: "in_progress" });
  }, hold === "parts" ? t("mech_wait_parts") : t("mech_pause"));
  const fixEnd = (iso) => run(() => api.post(`/repairs/${id}/clock-out`, { stopped_at: iso, hold: "pause" }), t("saved"));
  const addPart = (item, qty) => run(() => api.post(`/repairs/${id}/parts`, { item_id: item.id, quantity: qty }), `${item.name} ✓`);
  const tick = (kind, key, { done: d, remark }) => autosave(() => api.patch(`/repairs/${id}/worklist`, { kind, key, ...(d !== undefined ? { done: d } : {}), ...(remark !== undefined ? { remark } : {}) }));
  const finish = () => {
    if (!window.confirm(t("mech_done_confirm"))) return;
    run(async () => {
      if (running) await api.post(`/repairs/${id}/clock-out`, {});
      await api.put(`/repairs/${id}`, { status: "completed", hold: "none" });
    }, t("mechanicMarkedReady"));
  };

  if (!card) return <div className="p-8 text-center text-muted-foreground">…</div>;
  const locked = !!card.invoice_id && !card.reopened;
  const state = workState(card, running);

  return (
    <div className="max-w-4xl mx-auto space-y-4 pb-28" data-testid="mechanic-card">
      <MechanicHeader card={card} state={state} saving={saving} t={t} onBack={() => nav(-1)} />
      <MechanicTasks card={card} settings={settings} running={running} liveSec={liveSec} busy={busy} locked={locked} t={t} onStart={start} onStop={stop} onFixEnd={fixEnd} />
      <MechanicWorklist card={card} running={running} busy={busy || saving} locked={locked} t={t} onTick={tick} onStart={start} />
      <MechanicInspection card={card} lang={lang} t={t} locked={locked} onSave={(p) => autosave(() => api.put(`/repairs/${id}/inspection`, p))} onSent={done} />
      <MechanicParts card={card} inventory={inventory} lang={lang} t={t} busy={busy} locked={locked} onAdd={addPart} />
      <div className="-mt-1 px-1"><PartsRequestPanel card={card} t={t} busy={busy} locked={locked} onRequest={(p) => run(() => api.post(`/repairs/${id}/parts-request`, p), t("partsRequestSent"))} /></div>
      <section className="rounded-2xl border border-border bg-card p-4 sm:p-5 space-y-3" data-testid="mechanic-step-photos">
        <h2 className="font-semibold text-base md:text-lg flex items-center gap-2"><span className="h-8 w-8 rounded-full bg-primary text-primary-foreground grid place-items-center font-mono font-bold text-sm">5</span><Camera className="h-4 w-4 text-primary" />{t("mech_photos_title")}</h2>
        <RepairPhotos repairId={card.id} photos={card.photos || []} onChange={() => refetch()} />
      </section>
      <MechanicNotes card={card} t={t} locked={locked} onSave={(p) => autosave(() => api.put(`/repairs/${id}`, p))} />

      {!locked && (
        <div className="fixed bottom-0 inset-x-0 z-40 border-t border-border bg-background/95 backdrop-blur p-3">
          <div className="max-w-4xl mx-auto flex items-center gap-3">
            <div className="text-xs text-muted-foreground flex-1">{t("status")}: <span className="font-semibold">{t(`mech_state_${state}`)}</span></div>
            {card.status !== "completed" ? (
              <Button onClick={finish} disabled={busy} className="h-12 px-6 rounded-full bg-emerald-600 hover:bg-emerald-600/90 text-white" data-testid="mechanic-finish"><CheckCircle2 className="h-5 w-5 mr-2" />{t("mech_done")}</Button>
            ) : (
              <Button variant="outline" onClick={() => run(() => api.put(`/repairs/${id}`, { status: "in_progress" }))} disabled={busy} className="h-12 rounded-full" data-testid="mechanic-reopen"><Wrench className="h-4 w-4 mr-2" />{t("mech_reopen")}</Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
